import AbstractInterieur from "./AbstractInterieur";
import { v3 } from "oc-threejs/short3";
import Scenes from "oc-threejs/Scenes";

/**
 * Segmente ist eine Oberklasse und ermöglicht das einfache anlegen von weiteren Interieuren. Insbesondere solchen, die durch ein .obj geladen werden.
 * Es muss die variable parts erstellt werden, die folgende Inhalte haben kann:
 * this.parts = [
 *  {
 *  obj: {{Als String der Name des .obj, sodass es geladen werden kann}}
 *  origin: {{Als v3 der relative Ursprung in der BB}}
 *  quaternion: {{Als quat der quaternion - hiermit können generelle Rotationen abgebildet werden}}
 *  scale: {{Als v3 der scale, sodass z.B. generelle Spiegelungen oder scalings abgebildet werden können}}
 *  valid: {{Hiermit wird ermittelt, welches von vielen Objekten ausgewählt werden soll. Z.b. wenn es für verschiedene Größen mehrere Objekte gibt}}
 *          achsis: {{Als string die Achse die valid bestimmt}}
 *          tallerThan: {{Als number die kleinere Größe}}
 *          smallerThan: {{Als number die größere Größe}}
 * flip: {{Ermöglicht das flippen an den Seiten}}
 *          from: {{als quat die flipSide === 0 Rotation}}
 *          to: {{als quat die flipSide === 1 Rotation}}
 * mirror: {{Hiermit kann gespiegelt werden, entweder generell oder zu einer der beiden flip Positionen}}
 *          flipside: {{Evtl. die Flipside Position. NUR an dieser wird dann das Objekt gespiegelt}}
 *          with: {{v3 der die Spiegelung als scale umsetzt}}
 *  }
 * ]
 */

export default class Segmente extends AbstractInterieur {
    constructor(vsData) {
        super(vsData);
        //Ensures that loadOBJ is called once.
        this.loadedObj = false;
    }

    vsUpdate(force = false) {
        super.vsUpdate();
        //Ensures that loadOBJ is called once.
        !this.loadedObj ? this._loadObjectFile() : undefined;
        if (this.isVsChanged('size') || this.isVsChanged('flipSide') || force) {
            this.dearUpdate();
        }
    }


    _loadObjectFile() {
        this.loadedObj = true;
        this.parts.forEach((partValue, partIndex) => {
                this.loadOBJ({
                    path: partValue.obj,
                    artikelnummer: this.vsData.artikelnummer,
                    callback: object => {
                        //sets object for each part
                        partValue.object = object;
                        partValue.box = Scenes.boundingBox(object).clone();
                        this.dearUpdate();
                    }
                });
        });
    }

    /**
     * dear Update calls which are used by vsUpdate and once after _loadObjectFile
     */
    dearUpdate() {
        this.parts.forEach((partValue, partIndex) => {
            if (partValue.object) {
                //selects just valid objects (every interieur part can give options for that)
                if ((!partValue.valid || this.selectValids(partValue))) {
                    let origin = partValue.origin;
                    //creates and adds object
                    const object = partValue.object;
                    this.boxLocalO3.add(object);

                    //development
                    if (partValue.individualUpdate) {
                        partValue.individualUpdate(object, partValue, partIndex);
                    }
                    //changes Scale
                    if (partValue.scale) {
                        object.scale.copy(partValue.scale);
                    }
                    //changes quaternion
                    if (partValue.quaternion) {
                        object.quaternion.copy(partValue.quaternion);
                    }
                    //flips if chosen with given options
                    if (partValue.flip) {
                        if (this.vsData.flipSide === 0) {
                            object.quaternion.copy(partValue.flip.from);
                        } else {
                            object.quaternion.copy(partValue.flip.to);
                        }
                        origin = v3(this.vsData.flipSide, partValue.origin.y, partValue.origin.z);
                    }
                    //mirrors if chosen with given options
                    if (partValue.mirror) {
                        if (partValue.mirror.flipside !== undefined) {
                            if (this.vsData.flipSide === partValue.mirror.flipside) {
                                object.scale.copy(partValue.mirror.with);
                            }
                        } else {
                            object.scale.copy(partValue.mirror.with);
                        }
                    }

                    /**
                     * Falls es bei einem Segment der Fall ist, dass es anhand der validierung entscheidet, welches das passende ist
                     * muss auch das vsData.product angepasst werden, damit das Arrangement und die Preisberechnung korrekt verlaufen
                     * Kurz: Alle Elemente mit dem Typ des Klassennamens werden gesucht, daraus das solche mit dem passenden objName gesucht
                     * und gesetzt.
                     * Wichtig ist also dass Klassenname === Typ im Backend sowie partValue.obj === objName im Backend sind
                     * Nur falls es ein solches gibt darf es gemacht werden, da bspw. bei dem Garderobenlift keine Änderung vorgenommen werden darf,
                     * da es aus merhreren obj zusammengesetzt wird.
                     */
                    const product = APP.findProductsByType(this.constructor.name).filter(p => partValue.obj === p.objName)[0];
                    product ? this.vsData.product = product : null;

                    //fits in a interieur between 2 points
                    if(partValue.fitin) {
                        this.relativeFitin(partValue);
                    }

                    //Sets new position. E.g. when its flipped origin is changed.
                    const to = this.vsData.boxLocal.grid(origin);
                    const what = this.localChildBox(object).grid(origin);
                    object.position.add(to.sub(what).sub(v3(0, 0, 0)));
                }
                //not valid objects will be removed, because they're not selected
                else if (partValue.valid && (!this.selectValids(partValue))) {
                    Scenes.remove(partValue.object);
                }
            }
        });
    }

    /**
     * Used to select 1 items out of an array. 
     * Example: 4 same items for 1 interieur in different sizes: which one is valid for the given element size (x,y,z)?
     * @param {Object from Interieur which includes: achsis (String), tallerThan and smallerThan} valid
     * @returns {returns true if valid, or false if not valid for given options}
     */
    selectValids(partValue) {
        const valid = partValue.valid;
        const achsisString = valid.achsis;
        const tallerThan = valid.tallerThan;
        const smallerThan = valid.smallerThan;
        let achsis = this.vsData.size.x;
        if(this.vsData.element) {
            const elementSize = this.vsData.element.size;
            switch(achsisString) {
                case "x": achsis = elementSize.x; break;
                case "y": achsis = elementSize.y; break;
                case "z": achsis = elementSize.z; break;
            }
        }
        return achsis >= tallerThan && achsis < smallerThan;
    }

    //ACHTUNG: NOCH NICHT IMPLEMENTIERT, BISHER NUR QUICK AND DIRTY FÜR GARDEROBENLIFT
    /**
     * Methode soll dafür sorgen ein Interieur zu skalieren und zu positionieren,
     * sodass es zwischen zwei gegebene Punkte passt.
     * Beispiel hierfür ist die obere Stange des Garerobenlifts
     * @param {part} partValue 
     */
    relativeFitin(partValue) {
/*         const origin = partValue.origin;
        const offset = partValue.offset;
        const point = partValue.point; */
        const boxSize = partValue.box.getSize();
        const ratio = ((this.vsData.size.x -10 )/ boxSize.z) * 100;
        partValue.object.scale.copy(v3(100, 100, ratio));
    }

}
